/**
 * Numeric input cho các form New/Edit Futures Control.
 * - Chỉ nhận digits (+ 1 dấu chấm thập phân nếu `integer={false}`); chữ cái / `e` / `+` / `-` bị chặn.
 * - Khi blur: hiển thị format "standard" (thousand separator); khi focus: raw để dễ sửa.
 * - Right-aligned giống cột số trong datasheet.
 */
import { TextField, type TextFieldProps } from '@mui/material'
import { useState, type FocusEvent } from 'react'

type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: string
  onChange: (raw: string) => void
  /** true: chỉ digits; false (mặc định): digits + tối đa 1 dấu chấm. */
  integer?: boolean
}

const sanitize = (s: string, integer: boolean): string => {
  const cleaned = s.replace(integer ? /[^0-9]/g : /[^0-9.]/g, '')
  if (integer) return cleaned
  const dot = cleaned.indexOf('.')
  if (dot === -1) return cleaned
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '')
}

/**
 * Format kiểu Access "Standard": thousand separator, giữ nguyên phần thập phân user đã gõ
 * (kể cả trailing dot/zero) để khi focus lại không bị "snap" mất ký tự.
 */
const formatStandard = (raw: string, integer: boolean): string => {
  if (raw === '' || raw === '.' || raw === '-') return raw
  if (integer) {
    const n = Number(raw)
    return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : raw
  }
  const dot = raw.indexOf('.')
  if (dot === -1) {
    const n = Number(raw)
    return Number.isFinite(n) ? n.toLocaleString('en-US') : raw
  }
  const intPart = raw.slice(0, dot)
  const fracPart = raw.slice(dot + 1)
  const intNum = Number(intPart || '0')
  if (!Number.isFinite(intNum)) return raw
  return `${intNum.toLocaleString('en-US')}.${fracPart}`
}

export default function NumericInput({
  value,
  onChange,
  integer = false,
  inputProps,
  onFocus,
  onBlur,
  ...rest
}: Props) {
  const [focused, setFocused] = useState(false)
  const display = focused ? value : formatStandard(value, integer)

  return (
    <TextField
      {...rest}
      value={display}
      onChange={(e) => {
        // Khi blur (display có dấu phẩy) — strip dấu phẩy trước khi sanitize.
        const incoming = focused ? e.target.value : e.target.value.replace(/,/g, '')
        onChange(sanitize(incoming, integer))
      }}
      onFocus={(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(true)
        onFocus?.(e)
      }}
      onBlur={(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(false)
        onBlur?.(e)
      }}
      inputProps={{
        inputMode: integer ? 'numeric' : 'decimal',
        ...inputProps,
        style: { textAlign: 'right', ...(inputProps?.style as React.CSSProperties | undefined) },
      }}
    />
  )
}
